import React, { useEffect, useState } from "react";
import { Sparkles, Menu, X } from "lucide-react";
import {
  BRAND_NAME,
  PRIMARY_CTA_LABEL,
  handlePrimaryCta,
} from "@/lib/contact";

const NAV = [
  { id: "problem", label: "Why" },
  { id: "who-this-is-for", label: "Who it's for" },
  { id: "pricing", label: "Pricing" },
  { id: "faq", label: "FAQ" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-nav-950/80 border-b border-nav-800/80"
          : "bg-transparent"
      }`}
    >
      <div className="container-x flex h-16 sm:h-18 items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2"
        >
          <span className="grid place-items-center h-9 w-9 rounded-lg bg-brand text-white font-display font-bold">
            CB
          </span>
          <span className="font-display font-bold text-lg tracking-tight text-white">
            {BRAND_NAME}
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((item) => (
            <button
              key={item.id}
              onClick={() => goTo(item.id)}
              data-testid={`nav-${item.id}`}
              className="text-sm font-medium text-nav-300 hover:text-white transition-colors"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href="#contact"
            onClick={handlePrimaryCta}
            data-testid="header-primary-cta"
            className={`inline-flex items-center gap-2 rounded-full text-white px-4 sm:px-5 py-2.5 text-sm font-semibold transition-all duration-300 hover:scale-[1.03] ${
              scrolled
                ? "bg-brand hover:bg-brand-hover shadow-[0_8px_20px_-8px_rgba(91,91,255,0.6)]"
                : "bg-gradient-to-r from-[#7C3AED] to-[#EC4899] shadow-[0_10px_24px_-10px_rgba(124,58,237,0.65)] hover:shadow-[0_14px_32px_-8px_rgba(236,72,153,0.55)]"
            }`}
          >
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">{PRIMARY_CTA_LABEL}</span>
            <span className="inline sm:hidden">Demo</span>
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden grid place-items-center h-10 w-10 rounded-full border border-nav-700 text-white bg-nav-900/60"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-nav-800 bg-nav-950/95 backdrop-blur-xl">
          <div className="container-x py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <button
                key={item.id}
                onClick={() => goTo(item.id)}
                data-testid={`mobile-nav-${item.id}`}
                className="text-left py-3 text-base font-medium text-nav-200 hover:text-brand"
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
